import { Link } from "react-router-dom";
import {
  Search,
  Layers,
  ShieldAlert,
  Swords,
  ListChecks,
  Download,
  ArrowRight,
  CheckCircle2,
  Clock3,
  type LucideIcon,
} from "lucide-react";
import { PublicPageLayout, PageHero, SectionHead } from "./PublicPageLayout";
import { INTEL_AGENTS } from "@/components/brand";

/**
 * GuidesPage (/guides) - the "how to use NovaSynth" product guide.
 *
 * Real, useful content covering the whole workflow: getting started, how a
 * brief is built by the five specialists, reading the threat score, using
 * battlecards, the watchlist, and exporting. Several sections carry stable
 * anchor ids (#threat-scoring, #battlecards, #export) so the in-app footer's
 * Product links can deep-link straight to the relevant explainer.
 */

// Local nav so a reader can jump around a long page.
const GUIDE_NAV: { href: string; label: string }[] = [
  { href: "#getting-started", label: "Getting started" },
  { href: "#how-a-brief-is-built", label: "How a brief is built" },
  { href: "#threat-scoring", label: "Threat score" },
  { href: "#battlecards", label: "Battlecards" },
  { href: "#watchlist", label: "Watchlist" },
  { href: "#export", label: "Export" },
];

const GETTING_STARTED: { step: string; title: string; body: string; Icon: LucideIcon }[] = [
  {
    step: "01",
    title: "Type a competitor",
    body: "Enter a company name on the Analyze screen. No connectors, no setup, no data to upload first - the name is the whole input.",
    Icon: Search,
  },
  {
    step: "02",
    title: "Watch it assemble",
    body: "The specialists report live as they work. You see each finding land in real time rather than waiting on a spinner for a finished page.",
    Icon: Layers,
  },
  {
    step: "03",
    title: "Act on the brief",
    body: "You end with one threat-scored brief: the score, the reasoning, the counter-moves, and a citation behind every claim you can open and verify.",
    Icon: CheckCircle2,
  },
];

const READING_SCORE: { band: string; meaning: string; tint: string; chip: string }[] = [
  {
    band: "Low",
    meaning:
      "Limited overlap with your market, or no recent moves that change your position. Worth tracking, not worth reacting to this week.",
    tint: "text-emerald-300",
    chip: "bg-emerald-500/10 border-emerald-500/30",
  },
  {
    band: "Moderate",
    meaning:
      "Real overlap and active signals. Something is moving in a direction that touches your accounts. Put it on the agenda.",
    tint: "text-amber-300",
    chip: "bg-amber-500/10 border-amber-500/30",
  },
  {
    band: "High",
    meaning:
      "Direct competition plus momentum - a launch, a raise, a pricing change, or hiring that maps onto your roadmap. Brief the team now.",
    tint: "text-red-300",
    chip: "bg-red-500/10 border-red-500/30",
  },
];

export function GuidesPage({ authed = false }: { authed?: boolean }) {
  return (
    <PublicPageLayout authed={authed}>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-14 sm:pt-20 pb-8">
        <PageHero
          eyebrow="Product guide"
          title={
            <>
              Get the most out of{" "}
              <span className="ns-gradient-text">NovaSynth</span>
            </>
          }
          sub="Everything you need to turn a competitor name into a brief your team can act on - from your first run to exporting the finished assessment."
        />

        {/* On-page jump nav */}
        <nav
          aria-label="Guide sections"
          className="ns-reveal mt-9 flex flex-wrap justify-center gap-2"
          style={{ ["--ns-delay" as string]: "120ms" }}
        >
          {GUIDE_NAV.map((l) => (
            <a
              key={l.href}
              href={l.href}
              className="rounded-full border border-[#1a2640] bg-white/[0.03] px-3.5 py-1.5 text-xs font-medium text-slate-300 transition-colors hover:border-[#22d3ee]/40 hover:text-[#22d3ee] cursor-pointer"
            >
              {l.label}
            </a>
          ))}
        </nav>

        {/* Getting started */}
        <section id="getting-started" className="mt-16 scroll-mt-24">
          <SectionHead
            eyebrow="Getting started"
            title="Your first brief in three steps"
            sub="There is no onboarding maze. The fastest way to understand NovaSynth is to run one analysis and read the result."
          />
          <div className="mt-7 grid grid-cols-1 md:grid-cols-3 gap-4">
            {GETTING_STARTED.map((s, i) => (
              <div
                key={s.step}
                className="ns-reveal relative ns-glass rounded-2xl p-6"
                style={{ ["--ns-delay" as string]: `${120 + i * 90}ms` }}
              >
                <span className="absolute right-5 top-5 font-mono text-3xl font-bold text-white/5">
                  {s.step}
                </span>
                <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-brand-700/25 border border-brand-600/30 text-brand-300">
                  <s.Icon className="w-5 h-5" />
                </div>
                <h3 className="mt-4 text-base font-semibold text-white">{s.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-400">{s.body}</p>
              </div>
            ))}
          </div>
        </section>

        {/* How a brief is built - the five specialists */}
        <section id="how-a-brief-is-built" className="mt-20 scroll-mt-24">
          <SectionHead
            eyebrow="How a brief is built"
            title="Five specialists, one assessment"
            sub="Each specialist owns a corner of the picture and reports its findings as it works. The threat layer then fuses everything into a single, scored brief."
          />
          <div className="mt-7 grid grid-cols-1 sm:grid-cols-2 gap-4">
            {INTEL_AGENTS.map((a, i) => (
              <div
                key={a.key}
                className="ns-reveal group relative overflow-hidden rounded-2xl border border-[#1a2640] bg-[#0d1424]/70 p-6"
                style={{ ["--ns-delay" as string]: `${120 + i * 70}ms` }}
              >
                <div
                  className="pointer-events-none absolute -right-10 -top-10 h-28 w-28 rounded-full opacity-0 blur-2xl transition-opacity duration-300 group-hover:opacity-100"
                  style={{ background: a.hex }}
                  aria-hidden="true"
                />
                <div
                  className={`relative flex h-11 w-11 items-center justify-center rounded-xl border ${a.chip}`}
                >
                  <a.Icon className={`w-5 h-5 ${a.tint}`} />
                </div>
                <h3 className="relative mt-4 text-[15px] font-semibold text-white">{a.name}</h3>
                <p className="relative mt-2 text-sm leading-relaxed text-slate-400">{a.blurb}</p>
              </div>
            ))}
          </div>
          <p className="ns-reveal mt-5 flex items-center gap-2 text-xs text-slate-500">
            <ShieldAlert className="w-3.5 h-3.5 text-emerald-400" />
            Every claim in the finished brief carries a citation you can open and verify.
          </p>
        </section>

        {/* Reading the threat score */}
        <section id="threat-scoring" className="mt-20 scroll-mt-24">
          <SectionHead
            eyebrow="Threat score"
            title="Reading the threat score"
            sub="The score is relative to you, not an abstract rank. It weighs how much a competitor overlaps your market, how big the size gap is, whether you already track them, and what they have done recently - so a giant in a different segment can score lower than a focused rival in yours."
          />
          <div className="mt-7 grid grid-cols-1 md:grid-cols-3 gap-4">
            {READING_SCORE.map((b, i) => (
              <div
                key={b.band}
                className="ns-reveal ns-glass rounded-2xl p-6"
                style={{ ["--ns-delay" as string]: `${120 + i * 90}ms` }}
              >
                <span
                  className={`inline-flex items-center rounded-full border px-2.5 py-1 text-xs font-semibold ${b.chip} ${b.tint}`}
                >
                  {b.band}
                </span>
                <p className="mt-3 text-sm leading-relaxed text-slate-400">{b.meaning}</p>
              </div>
            ))}
          </div>
          <div
            className="ns-reveal mt-5 rounded-2xl border border-[#1a2640] bg-white/[0.02] p-5"
            style={{ ["--ns-delay" as string]: "400ms" }}
          >
            <p className="text-sm leading-relaxed text-slate-400">
              Open the threat panel to see <span className="text-slate-200">why it matters to us</span>:
              each dimension is shown on its own so you can tell whether the score is driven by market
              overlap, recent momentum, or both. Nothing is a black box - the reasoning is right there
              next to the number.
            </p>
          </div>
        </section>

        {/* Battlecards */}
        <section id="battlecards" className="mt-20 scroll-mt-24">
          <SectionHead
            eyebrow="Battlecards"
            title="Using the battlecard"
            sub="Every brief includes a battlecard built for the moment you are in front of a prospect: where the competitor is strong, where they are exposed, and the lines that hold up under pushback."
          />
          <div className="mt-7 grid grid-cols-1 md:grid-cols-3 gap-4">
            <GuideTile
              Icon={Swords}
              title="Strengths and gaps"
              body="A balanced read - what the competitor genuinely does well, and the gaps you can lean into without overstating your hand."
            />
            <GuideTile
              Icon={ShieldAlert}
              title="Objection handling"
              body="The talk tracks that answer the questions you actually hear in a deal, each anchored to a cited source rather than a guess."
            />
            <GuideTile
              Icon={Download}
              title="Deal-ready"
              body="Concise enough to skim before a call and clear enough to forward to a teammate who was not in the room."
            />
          </div>
        </section>

        {/* Watchlist */}
        <section id="watchlist" className="mt-20 scroll-mt-24">
          <SectionHead
            eyebrow="Watchlist"
            title="Tracking competitors over time"
            sub="Add the companies that matter to your watchlist and NovaSynth keeps each one a click away, with the history of every brief you have run."
          />
          <div className="mt-7 grid grid-cols-1 md:grid-cols-3 gap-4">
            <GuideTile
              Icon={ListChecks}
              title="One place for your set"
              body="Your tracked competitors live on the watchlist so the next analysis is a tap, not a re-type."
            />
            <GuideTile
              Icon={Clock3}
              title="Run history"
              body="Every brief is kept, so you can see what changed since last time and how a competitor's posture is trending."
            />
            <GuideTile
              Icon={Layers}
              title="Re-run on demand"
              body="Re-run any tracked competitor to refresh the picture before a board update, a deal, or a launch response."
            />
          </div>
        </section>

        {/* Export */}
        <section id="export" className="mt-20 scroll-mt-24">
          <SectionHead
            eyebrow="Export"
            title="Exporting and sharing a brief"
            sub="When a brief is ready, take it with you. Export produces a clean, printable version - the content only, on white, with the live chrome and backdrop stripped out."
          />
          <div
            className="ns-reveal mt-7 rounded-2xl border border-[#1a2640] bg-[#0d1424]/70 p-6"
            style={{ ["--ns-delay" as string]: "120ms" }}
          >
            <div className="flex items-start gap-4">
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-brand-700/25 border border-brand-600/30 text-brand-300">
                <Download className="w-5 h-5" />
              </div>
              <div>
                <h3 className="text-base font-semibold text-white">A brief your team will read</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-400">
                  The exported document keeps the threat score, the reasoning, the battlecard, and the
                  citations - formatted for ink so it reads cleanly in a doc, a deck appendix, or a
                  printed handout. Share it as-is; nothing about the source is left to guess at.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="mt-20">
          <div className="ns-reveal relative overflow-hidden rounded-3xl border border-brand-600/30 bg-gradient-to-br from-brand-700/25 via-[#0d1424] to-[#22d3ee]/10 p-10 sm:p-12 text-center">
            <div className="ns-aurora" aria-hidden="true" />
            <div className="relative">
              <h2 className="font-mono text-2xl sm:text-3xl font-bold tracking-tight text-white">
                Ready to run one yourself?
              </h2>
              <p className="mx-auto mt-3 max-w-xl text-sm leading-relaxed text-slate-300">
                The fastest way to learn NovaSynth is to point it at a competitor and watch the brief
                build itself.
              </p>
              <div className="mt-6 flex flex-wrap justify-center gap-3">
                <Link
                  to="/signup"
                  className="group inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-brand-600 to-brand-500 px-6 py-3 text-sm font-semibold text-white shadow-glow-brand-sm transition-all duration-200 hover:from-brand-500 hover:to-brand-400 cursor-pointer min-h-[44px]"
                >
                  Get started
                  <ArrowRight className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-0.5" />
                </Link>
                <Link
                  to="/playbooks"
                  className="inline-flex items-center gap-2 rounded-xl border border-[#1a2640] bg-white/[0.03] px-6 py-3 text-sm font-semibold text-slate-200 transition-colors hover:border-[#22d3ee]/40 hover:text-[#22d3ee] cursor-pointer min-h-[44px]"
                >
                  Browse playbooks
                </Link>
              </div>
            </div>
          </div>
        </section>
      </div>
    </PublicPageLayout>
  );
}

function GuideTile({ Icon, title, body }: { Icon: LucideIcon; title: string; body: string }) {
  return (
    <div className="ns-reveal ns-glass rounded-2xl p-6">
      <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-white/[0.04] border border-[#1a2640] text-[#22d3ee]">
        <Icon className="w-5 h-5" />
      </div>
      <h3 className="mt-4 text-base font-semibold text-white">{title}</h3>
      <p className="mt-2 text-sm leading-relaxed text-slate-400">{body}</p>
    </div>
  );
}
